import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon,
  ClockIcon, BanknotesIcon, CalendarDaysIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ty-root * { box-sizing:border-box; }
  .ty-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tyFade .4s ease both; }
  @keyframes tyFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .ty-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .ty-back:hover { color:#3b6ef8; }
  .ty-back svg { width:14px; height:14px; stroke-width:2.5; }

  .ty-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:1rem; }
  .ty-teacher { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .ty-avatar  { width:46px; height:46px; border-radius:13px; background:#ecfdf5; color:#10c98e; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .ty-name    { font-size:1.3rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .ty-empid   { font-family:'JetBrains Mono',monospace; font-size:.72rem; color:#9094a8; margin-top:2px; }

  .ty-controls { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

  .year-nav { display:flex; align-items:center; gap:8px; background:white; border:1px solid #e8ebf4; border-radius:12px; padding:6px 10px; }
  .year-btn  { width:30px; height:30px; border-radius:8px; border:1px solid #e4e7f0; background:white; color:#6b7194; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:all .15s; }
  .year-btn:hover { background:#eef2ff; border-color:#3b6ef8; color:#3b6ef8; }
  .year-btn:disabled { opacity:.35; cursor:not-allowed; }
  .year-btn svg { width:14px; height:14px; stroke-width:2.5; }
  .year-label { font-family:'JetBrains Mono',monospace; font-size:.9rem; font-weight:600; color:#1a1d2e; min-width:52px; text-align:center; }

  /* Export dropdown */
  .export-wrap { position:relative; display:inline-flex; }
  .export-btn  { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:1.5px solid #e0e4f0; border-radius:10px; background:white; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; color:#6b7194; transition:all .15s; }
  .export-btn:hover { border-color:#3b6ef8; color:#3b6ef8; background:#f5f7ff; }
  .export-btn svg { width:15px; height:15px; stroke-width:2; }
  .export-menu { position:absolute; top:calc(100% + 6px); right:0; background:white; border:1px solid #e8ebf4; border-radius:12px; box-shadow:0 8px 24px rgba(0,0,0,.1); min-width:150px; z-index:100; overflow:hidden; }
  .export-menu-item { display:flex; align-items:center; gap:8px; padding:9px 14px; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; color:#1a1d2e; cursor:pointer; border:none; background:none; width:100%; text-align:left; transition:background .12s; }
  .export-menu-item:hover { background:#f5f7ff; color:#3b6ef8; }
  .export-menu-item svg { width:14px; height:14px; stroke-width:2; color:#9094a8; }

  .month-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:1rem; }
  .month-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem; cursor:pointer; position:relative; overflow:hidden; transition:transform .18s,box-shadow .18s,border-color .18s; animation:tyFade .4s ease both; }
  .month-card:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(59,110,248,.1); border-color:#c8d5fd; }
  .month-card.future { opacity:.5; cursor:default; pointer-events:none; }
  .mc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:.9rem; gap:6px; flex-wrap:wrap; }
  .mc-name   { font-size:.88rem; font-weight:700; color:#0f1123; }
  .mc-badge  { display:inline-flex; align-items:center; gap:4px; font-size:.65rem; font-weight:700; padding:3px 8px; border-radius:99px; white-space:nowrap; }
  .mc-badge svg { width:11px; height:11px; stroke-width:2.5; }
  .mc-badge.finalized { background:#ecfdf5; color:#10c98e; }
  .mc-badge.pending   { background:#f0f2f7; color:#9094a8; }
  .mc-badge.paid      { background:#eef2ff; color:#3b6ef8; }
  .mc-badge.unpaid    { background:#fef2f2; color:#ef4444; }
  .mc-days   { display:flex; align-items:center; gap:5px; font-size:.72rem; color:#9094a8; margin-bottom:6px; }
  .mc-days svg { width:13px; height:13px; stroke-width:1.8; }
  .mc-salary { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:600; color:#10c98e; }
  .mc-salary.empty { color:#c0c5dc; font-weight:400; }

  .ty-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  /* ── Responsive ── */
  @media (max-width:900px)  { .month-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px)  {
    .ty-name { font-size:1.1rem; }
    .ty-header { gap:.8rem; }
  }
  @media (max-width:600px)  { .month-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:380px)  { .month-grid { grid-template-columns:1fr; } }
`;

const initials = (name='') => name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase() || '?';
const fmt      = (n) => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:0,maximumFractionDigits:0})}`;

// ── Export helpers ────────────────────────────────────────────────────────────
const exportCSV = (rows, filename) => {
  if (!rows.length) return;
  const hdrs = Object.keys(rows[0]);
  const esc  = v => { const s=v==null?'':String(v); return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s; };
  const csv  = [hdrs.join(','), ...rows.map(r=>hdrs.map(h=>esc(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], {type:'text/csv;charset=utf-8;'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href=url; a.download=filename+'.csv'; a.click();
  URL.revokeObjectURL(url);
};

const exportXLSX = async (rows, filename) => {
  if (!rows.length) return;
  try {
    const XLSX = await import('xlsx');
    const ws   = XLSX.utils.json_to_sheet(rows);
    const wb   = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename+'.xlsx');
  } catch { exportCSV(rows, filename); }
};
// ─────────────────────────────────────────────────────────────────────────────

const StaffSalaryYear = () => {
  const { staffId: teacherId } = useParams();
  const [searchParams] = useSearchParams();
  const role     = searchParams.get('role')     || 'teacher';
  const isPortal = searchParams.get('isPortal') !== 'false';
  const navigate = useNavigate();
  const now      = new Date();

  const [year,        setYear]       = useState(now.getFullYear());
  const [data,        setData]       = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [exportOpen,  setExportOpen] = useState(false);
  const [myUserId, setMyUserId] = useState(null);

  useEffect(() => {
    api.get('/admin/me').then(r => setMyUserId(String(r.data?._id||''))).catch(()=>{});
  }, []);
  useEffect(() => { fetchYear(); }, [teacherId, year]);

  // Close export menu on outside click
  useEffect(() => {
    if (!exportOpen) return;
    const handler = () => setExportOpen(false);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportOpen]);

  const fetchYear = async () => {
    try {
      setLoading(true);
      if (role === 'teacher' && isPortal) {
        const { data: res } = await api.get(`/admin/salary/teacher/${teacherId}/year/${year}`);
        setData(res);
      } else {
        // Build year summary from monthly previews
        const months = [];
        for (let m = 1; m <= 12; m++) {
          const monthStr = `${year}-${String(m).padStart(2,'0')}`;
          try {
            const { data: r } = await api.get(`/admin/salary/preview/${monthStr}?staffId=${teacherId}&role=${role}&isPortal=${isPortal}`);
            months.push({
              month: monthStr,
              label: new Date(year, m-1, 1).toLocaleString('en-US', {month:'long'}),
              year,
              isFinalized: !!r.savedRecord,
              status:      r.savedRecord?.status || null,
              netSalary:   r.savedRecord?.netSalary || null,
              daysPresent: r.savedRecord?.daysPresent || 0,
              finalizedAt: r.savedRecord?.finalizedAt || null,
            });
          } catch {
            months.push({ month:monthStr, label:new Date(year,m-1,1).toLocaleString('en-US',{month:'long'}), year, isFinalized:false, status:null, netSalary:null, daysPresent:0, finalizedAt:null });
          }
        }
        setData({ teacher:{ teacherId, name:'Staff Member', employeeId:'' }, year, months });
      }
    } catch { toast.error('Failed to load salary data'); }
    finally   { setLoading(false); }
  };

  const isFuture = (monthStr) => {
    const [y, m] = monthStr.split('-').map(Number);
    return new Date(y, m-1, 1) > new Date(now.getFullYear(), now.getMonth(), 1);
  };

  // ── Build export rows ─────────────────────────────────────────────────────
  const buildRows = () => {
    if (!data?.months) return [];
    return data.months.map(m => ({
      Name:          data.teacher?.name || '',
      'Employee ID': data.teacher?.employeeId || '',
      Month:         m.month,
      'Days Present':m.daysPresent || 0,
      Status:        m.isFinalized ? (m.status || 'finalized') : 'pending',
      'Net Salary':  m.netSalary ? Number(m.netSalary).toFixed(2) : '',
      'Finalized At':m.finalizedAt ? new Date(m.finalizedAt).toLocaleDateString('en-PK') : '',
    }));
  };

  const handleExport = async (fmt_) => {
    setExportOpen(false);
    const rows = buildRows();
    if (!rows.length) { toast.error('No data to export'); return; }
    const name = data?.teacher?.name?.replace(/\s+/g,'-') || 'staff';
    const filename = `salary-year-${name}-${year}`;
    fmt_ === 'csv' ? exportCSV(rows, filename) : await exportXLSX(rows, filename);
  };

  return (
    <div className="ty-root"><style>{style}</style>

      <button className="ty-back" onClick={() => navigate(searchParams.get('role') ? '/staff-salary' : '/salary-records')}>
        <ChevronLeftIcon />Back to Teachers
      </button>

      <div className="ty-header">
        {data && (
          <div className="ty-teacher">
            <div className="ty-avatar">{initials(data.teacher?.name)}</div>
            <div>
              <div className="ty-name">{data.teacher?.name}</div>
              {data.teacher?.employeeId && <div className="ty-empid">{data.teacher.employeeId}</div>}
            </div>
          </div>
        )}

        <div className="ty-controls">
          {/* ── Export dropdown ── */}
          <div className="export-wrap" onMouseDown={e => e.stopPropagation()}>
            <button className="export-btn" onClick={() => setExportOpen(o => !o)}>
              <ArrowDownTrayIcon />Export {year}
            </button>
            {exportOpen && (
              <div className="export-menu">
                <button className="export-menu-item" onClick={() => handleExport('csv')}><ArrowDownTrayIcon />CSV</button>
                <button className="export-menu-item" onClick={() => handleExport('xlsx')}><ArrowDownTrayIcon />XLSX</button>
              </div>
            )}
          </div>

          {/* ── Year navigator ── */}
          <div className="year-nav">
            <button className="year-btn" onClick={() => setYear(y => y-1)}><ChevronLeftIcon /></button>
            <span className="year-label">{year}</span>
            <button className="year-btn" onClick={() => setYear(y => y+1)}
              disabled={year >= now.getFullYear()}>
              <ChevronRightIcon />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ty-loading"><div className="spinner"/>Loading…</div>
      ) : (
        <div className="month-grid">
          {data?.months?.map((m, i) => (
            <div key={m.month}
              className={`month-card${isFuture(m.month) ? ' future' : ''}`}
              style={{ animationDelay:`${i*.03}s`, opacity: String(teacherId)===String(myUserId) ? 0.5 : 1, cursor: String(teacherId)===String(myUserId) ? 'not-allowed' : undefined }}
              style={{ animationDelay:`${i*.03}s` }}
              onClick={() => !isFuture(m.month) && String(teacherId) !== String(myUserId) && navigate(`/staff-salary/${teacherId}/${m.month}?role=${role}&isPortal=${isPortal}`)}>

              <div className="mc-top">
                <div className="mc-name">{m.label}</div>
                {m.isFinalized ? (
                  <span className={`mc-badge ${m.status==='paid' ? 'paid' : 'unpaid'}`}>
                    {m.status==='paid' ? '✓ Paid' : '● Unpaid'}
                  </span>
                ) : isFuture(m.month) ? (
                  <span className="mc-badge pending">Upcoming</span>
                ) : (
                  <span className="mc-badge pending"><ClockIcon />Pending</span>
                )}
              </div>
              <div className="mc-days"><CalendarDaysIcon />{m.daysPresent} day{m.daysPresent!==1?'s':''} present</div>
              <div className={`mc-salary${!m.netSalary?' empty':''}`}>{m.netSalary ? fmt(m.netSalary) : 'Not finalized'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StaffSalaryYear;